"use client";

import React, { createContext, useContext, useMemo, ReactNode } from "react";
import useBacklogOriginActions from "../hooks/useBacklogOriginActions";

interface BacklogContextValue {
  // Confirmation dialogs
  confirmOriginDeletion: ReturnType<
    typeof useBacklogOriginActions
  >["confirmOriginDeletion"];

  // Origin action handlers
  originHandlers: Omit<
    ReturnType<typeof useBacklogOriginActions>,
    "confirmOriginDeletion"
  >;
}

const BacklogContext = createContext<BacklogContextValue | undefined>(
  undefined,
);

export function BacklogContextProvider({ children }: { children: ReactNode }) {
  const {
    confirmOriginDeletion,
    ...originHandlers
  } = useBacklogOriginActions();

  // Memoize the context value to prevent unnecessary re-renders
  const contextValue = useMemo(
    () => ({
      confirmOriginDeletion,
      originHandlers,
    }),
    [confirmOriginDeletion, originHandlers],
  );

  return (
    <BacklogContext.Provider value={contextValue}>
      {children}
    </BacklogContext.Provider>
  );
}

export function useBacklogContext() {
  const context = useContext(BacklogContext);
  if (!context) {
    throw new Error(
      "useBacklogContext must be used within BacklogContextProvider",
    );
  }
  return context;
}
